'use client';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@/navigation';
import { Entry } from 'contentful';
import {
  TypeImage,
  TypeLink_external,
  TypeLink_externalFields,
  TypeLink_internal,
  TypeLink_internalFields,
  TypeListFields,
} from '@/core/generated-types';
import { Image } from '@/components/ui/Image';
import Cart from '@/components/ui/Cart/Cart';
import useAuth, { AuthState } from '@/context/AuthContext';
import classNames from 'classnames';
import { shouldHideWithLocaleOverride } from '@/utils/localeOverride';
import dynamic from 'next/dynamic';
import { NavMenuItem } from './NavMenuItem';
import { NavMenuItemDropdown } from './NavMenuItemDropdown';
import { motion } from 'framer-motion';
import { UniversalCta } from '../UniversalCta';
import { useNavbarAnimations } from '@/components/homepage-us/NavMenu/utils';

/*
  Import the SignInMenuItem as a dynamic component since it holds conditional
  TSX based on the user context which is always null on the server and will
  cause hydration errors.
*/
const SignInMenuItem = dynamic(() => import('./SignInMenuItem'), {
  ssr: false,
});

export interface NavMenuProps {
  linkSection1: Entry<
    TypeLink_externalFields | TypeLink_internalFields | TypeListFields
  >[];
  linkSection2:
    | Entry<
        TypeLink_externalFields | TypeLink_internalFields | TypeListFields
      >[]
    | undefined;
  userMenuLoggedOut:
    | Entry<
        TypeLink_externalFields | TypeLink_internalFields | TypeListFields
      >[]
    | undefined;
  userMenuLoggedIn:
    | Entry<
        TypeLink_externalFields | TypeLink_internalFields | TypeListFields
      >[]
    | undefined;
  logoutText: string;
  cartText: string;
  logo: TypeImage;
  playKitsByAgeLinks?: Entry<Record<string, any>>[];
  playKitsDropdownLinks?: Entry<Record<string, any>>[];
  playKistByAgeCta?: TypeLink_external;
}

const LOGOUT_URL = `https://shop.${process.env.NEXT_PUBLIC_BACKEND_DOMAIN}/account/logout`;

export const buildMenu = (
  menuItems: any[],
  user?: AuthState | null,
  logoutText?: string,
  onClickSignOut?: () => void,
) => {
  const menuItemsDisplayed = menuItems?.filter(
    (menuItem) => !shouldHideWithLocaleOverride(menuItem.fields.localeOverride),
  );

  return menuItemsDisplayed.map((menuItem, i) => {
    return (
      <NavMenuItem
        key={i}
        {...{ menuItem, i, user, logoutText, onClickSignOut }}
      />
    );
  });
};

export const buildAgeBasedMenu = (
  menuItems: TypeLink_internal[],
  playKitsByAgeLinks: Entry<Record<string, any>>[],
  playKitsDropdownLinks: Entry<Record<string, any>>[],
  user?: AuthState | null,
  logoutText?: string,
  onClickSignOut?: () => void,
) => {
  const menuItemsDisplayed = menuItems?.filter(
    (menuItem) => !shouldHideWithLocaleOverride(menuItem.fields.localeOverride),
  );

  return menuItemsDisplayed.map((menuItem, i) => {
    if (menuItem.fields.linkText === 'The Play Kits') {
      return (
        <NavMenuItemDropdown
          key={menuItem.sys.id}
          menuItem={menuItem}
          playKitsByAgeLinks={playKitsByAgeLinks}
          playKitsDropdownLinks={playKitsDropdownLinks}
        />
      );
    }

    return (
      <NavMenuItem
        key={menuItem.sys.id}
        {...{ menuItem, i, user, logoutText, onClickSignOut }}
      />
    );
  });
};

const NavMenu: FC<NavMenuProps> = ({
  linkSection1,
  linkSection2,
  userMenuLoggedOut,
  userMenuLoggedIn,
  logoutText,
  cartText,
  logo,
  playKitsByAgeLinks,
  playKitsDropdownLinks,
  playKistByAgeCta,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMouseOverMenu, setIsMouseOverMenu] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);
  const { ctaOpacity, isCTAHidden } = useNavbarAnimations({
    isDesktop: true,
  });

  const shouldHideCta = useMemo(() => isCTAHidden, [isCTAHidden]);

  const { user, signOut } = useAuth();
  const onClickSignOut = async () => {
    await signOut();
    window.location.assign(LOGOUT_URL);
  };

  useEffect(() => {
    const onScroll = () => {
      const { scrollTop } = window.document.documentElement;
      if (scrollTop <= 0 && isScrolled) {
        setIsScrolled(false);
      } else if (scrollTop > 0 && !isScrolled) {
        setIsScrolled(true);
      }

      // Closes submenu when mouse is not over the navigation.
      if (rootRef.current?.contains(document.activeElement)) {
        (document.activeElement as HTMLElement).blur();
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [isScrolled, isMouseOverMenu]);

  return (
    <nav
      onMouseOver={() => setIsMouseOverMenu(true)}
      onMouseLeave={() => setIsMouseOverMenu(false)}
      data-testid="navmenu"
      ref={rootRef}
      className={classNames(
        'z-50 hidden flex-wrap items-center justify-between bg-white lg:block ',
        {
          'shadow-lg': isScrolled,
        },
      )}
    >
      <div className="relative grid grid-cols-[40%_20%_40%] items-center py-5 lg:px-8 xl:px-12">
        {/* left slot */}
        <div className="flex flex-none items-center justify-between">
          {playKitsByAgeLinks &&
          playKitsByAgeLinks?.length > 0 &&
          linkSection1 ? (
            <ul className="menu menu-horizontal font-bold">
              {buildAgeBasedMenu(
                linkSection1 as TypeLink_internal[],
                playKitsByAgeLinks as Entry<Record<string, any>>[],
                playKitsDropdownLinks as Entry<Record<string, any>>[],
              )}
            </ul>
          ) : (
            <ul className="menu menu-horizontal font-bold">
              {linkSection1 && buildMenu(linkSection1)}
            </ul>
          )}
        </div>

        {/* center slot */}
        <div className="flex justify-center md:mb-0">
          <Link href={'/'}>
            <figure>
              {logo && (
                <Image imageData={logo} width={167} height={18} alt="" />
              )}
            </figure>
          </Link>
        </div>

        {/* right slot */}
        <div className="flex flex-none items-center justify-end space-x-4">
          <div className="h-[50px]">
            {playKistByAgeCta && !shouldHideCta && (
              <motion.div
                style={{ opacity: ctaOpacity }}
                data-testid="navMenu-CTA"
              >
                <UniversalCta
                  link={playKistByAgeCta}
                  className="px-5 lg:px-1 lg:min-w-[120px] xl:px-5 -mt-3 "
                />
              </motion.div>
            )}
          </div>
          <ul className="menu menu-horizontal font-bold">
            {linkSection2 && buildMenu(linkSection2)}
          </ul>
          <SignInMenuItem
            buildMenu={buildMenu}
            user={user}
            userMenuLoggedIn={userMenuLoggedIn}
            userMenuLoggedOut={userMenuLoggedOut}
            logoutText={logoutText}
            onClickSignOut={onClickSignOut}
          />
          <Cart cartText={cartText} />
        </div>
      </div>
    </nav>
  );
};

export default NavMenu;
